import {
  Component,
  ChangeDetectionStrategy,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  BusinessTypesService,
  CommitteeService,
  JobTitleService,
  OrganisationService,
  UserService,
} from '@src/core/services';
import { FilterFieldsOptions, FilterItem } from '@src/app/modules/search-users/types';
import { CommitteeUI } from '@src/models';
import { EnvService } from '@src/app/modules/env';
import { CustomNamesService } from '@src/app/modules/custom-name-tabs';
import { BaseResizableComponent } from '@src/app/modules/base';

import { DEFAULT_FILTER_STORE, DEFAULT_FILTER_PARAMS } from './constants';
import { FilterParams, FilterStoreItem, FilterStoreKeys } from './types';

@Component({
  selector: 'search-users-filters',
  templateUrl: './search-users-filters.component.html',
  styleUrls: ['./search-users-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchUsersFiltersComponent extends BaseResizableComponent implements OnChanges {
  private readonly jobTitlesService = inject(JobTitleService);
  private readonly businessTypesService = inject(BusinessTypesService);
  private readonly organisationService = inject(OrganisationService);
  private readonly committeeService = inject(CommitteeService);
  private readonly userService = inject(UserService);
  private readonly env = inject(EnvService);
  private readonly customNamesService = inject(CustomNamesService);

  /** Поля, скрытые в фильтре */
  @Input() excludeFilterFields: FilterFieldsOptions[] = [];

  @Output() filtersChange = new EventEmitter<FilterParams>();

  filtersCount = 0;
  isPossibleToApply = false;
  store = { ...DEFAULT_FILTER_STORE };
  open$ = new BehaviorSubject<boolean>(false);

  appliedFilterCount = 0;
  appliedStore = { ...DEFAULT_FILTER_PARAMS };

  constructor() {
    super();

    this.jobTitlesService.jobTitles$.pipe(takeUntil(this.destroyed$$)).subscribe(items => {
      this.store.jobs.items = items.map(item => ({ id: item.jobTitleId ?? '', label: item.jobTitleName ?? '' }));
      this.cdr.markForCheck();
    });

    this.businessTypesService.businessTypes$.pipe(takeUntil(this.destroyed$$)).subscribe(items => {
      this.store.businessTypes.items =
        items?.map(item => ({
          id: item.businessTypeId ?? '',
          label: item.businessTypeName ?? '',
        })) ?? [];
      this.cdr.markForCheck();
    });

    this.organisationService.organisationList$.pipe(takeUntil(this.destroyed$$)).subscribe(items => {
      this.store.organizations.items =
        items?.map(item => ({
          id: item.id ?? '',
          label: item.shortName ?? '',
          subLabel: item.fullName,
        })) ?? [];
      this.cdr.markForCheck();
    });

    this.committeeService.committeesTree$.pipe(takeUntil(this.destroyed$$)).subscribe(items => {
      const committees: FilterItem[] = [];
      this.getCommittees(committees, items ?? []);

      this.store.committees.items = committees;
      this.cdr.markForCheck();
    });

    this.userService
      .getAllContactsForUserDescriptionsForOrganisations()
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(names => {
        this.store.contacts.items = names.map(name => ({
          id: '', // TODO: тут будет храниться значение поля
          label: name,
        }));
        this.cdr.markForCheck();
      });

    this.open$.pipe(takeUntil(this.destroyed$$)).subscribe(open => {
      if (open) {
        this.restoreStore();
      }

      this.onCheckFiltersCount();
    });
  }

  get isMobilePlatform(): boolean {
    return this.env.isMobilePlatform;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.jobTitlesService.getJobTitles();
    this.businessTypesService.getBusinessTypes();
    this.organisationService.getOrganisationList();
    this.committeeService.loadCommitteesTreeData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.excludeFilterFields) {
      this.store = { ...DEFAULT_FILTER_STORE };
      this.appliedStore = { ...DEFAULT_FILTER_PARAMS };

      if (this.excludeFilterFields.length) {
        this.excludeFilterFields.forEach(value => {
          delete this.store[value];
          delete this.appliedStore[value];
        });
      }
    }
  }

  override ngOnDestroy() {
    this.onReset();

    super.ngOnDestroy();
  }

  onToggleDropdown() {
    this.open$.next(!this.open$.value);
  }

  onObscured(obscured: boolean): void {
    if (obscured) {
      this.open$.next(false);
    }
  }

  onActiveZone(active: boolean): void {
    this.open$.next(active && this.open$.value);
  }

  onApply() {
    this.onCheckFiltersCount();

    const result = Object.keys(this.store).reduce<FilterParams>((acc, key) => {
      acc[key as FilterStoreKeys] = this.store[key as FilterStoreKeys].result;

      return acc;
    }, DEFAULT_FILTER_PARAMS);

    this.appliedStore = JSON.parse(JSON.stringify(result));
    this.appliedFilterCount = this.filtersCount;
    this.open$.next(false);

    this.cdr.markForCheck();

    this.filtersChange.emit(result);
  }

  onReset() {
    this.clearStore();

    this.onApply();
  }

  onCheckFiltersCount() {
    this.filtersCount = Object.values(this.store).reduce((acc, item) => {
      return acc + item.result.length;
    }, 0);

    if (this.filtersCount !== this.appliedFilterCount) {
      this.isPossibleToApply = true;
    } else {
      this.isPossibleToApply = Object.keys(this.store).some(key => {
        const storeResult = this.store[key as FilterStoreKeys].result;
        const appliedResult = this.appliedStore[key as FilterStoreKeys];

        if (storeResult.length !== appliedResult.length) {
          return true;
        }

        return storeResult.some(
          storeItem =>
            !appliedResult.find(
              appliedItem => appliedItem.id === storeItem.id && appliedItem.label === storeItem.label,
            ),
        );
      });
    }

    this.cdr.markForCheck();
  }

  getStoreValues(): FilterStoreItem[] {
    return Object.values(this.store);
  }

  getTitle(key: string): string {
    const customNameItem = this.customNamesService.getItem(key as any); // TODO: Избавиться от any

    return customNameItem?.title ?? key;
  }

  private clearStore() {
    Object.keys(this.store).forEach(key => {
      this.store[key as FilterStoreKeys].result = [];
    });
  }

  private restoreStore() {
    Object.keys(this.store).forEach(key => {
      this.store[key as FilterStoreKeys].result = this.appliedStore[key as FilterStoreKeys].slice();
    });
  }

  private getCommittees(store: FilterItem[], list: CommitteeUI[], parent?: string[]) {
    list.forEach(item => {
      parent = parent ?? [];

      if (!item.parentCommitteeId) {
        parent = [item.name ?? ''];
      } else {
        parent.push(item.name ?? '');
      }

      store.push({
        id: item.id ?? '',
        label: item.name ?? '',
        avatarId: item.photoId,
        subLabel: parent.length ? parent.join(' > ') : undefined,
      });
      if (item.subCommitties?.length) {
        this.getCommittees(store, item.subCommitties, parent);
      }
    });
  }
}
